import { useEffect, useMemo, useState } from "react";
import { Dialog as DialogPrimitive } from "radix-ui";
import { X, Search } from "lucide-react";
import { useGuidesStore } from "@/stores/useGuidesStore";
import { useQuickStartStore } from "@/stores/useQuickStartStore";
import {
  walkthroughsByGroup,
  WALKTHROUGH_GROUP_ORDER,
  type WalkthroughEntry,
  type WalkthroughGroup,
} from "@/lib/guideRegistry";
import { cn } from "@/lib/utils";
import {
  areSuggestionsDisabled,
  setSuggestionsDisabled,
  SUGGESTIONS_PREF_EVENT,
} from "@/lib/suggestionPrefs";

const GROUP_LABELS: Record<WalkthroughGroup, string> = {
  "start-here": "Start here",
  foundations: "Foundations",
  corpus: "Corpus building",
  analysis: "Analysis",
  output: "Output",
};

export function GuidesCatalogDrawer() {
  const open = useGuidesStore((s) => s.catalogOpen);
  const setOpen = useGuidesStore((s) => s.setCatalogOpen);
  const openWalkthrough = useGuidesStore((s) => s.openWalkthrough);
  const openAtFeatureId = useGuidesStore((s) => s.openAtFeatureId);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!openAtFeatureId) return;
    // Defer to allow drawer to render first
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-feature-id="${openAtFeatureId}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
    return () => clearTimeout(t);
  }, [openAtFeatureId]);

  const grouped = useMemo(() => walkthroughsByGroup(), []);

  const filtered = useMemo(() => {
    if (!query.trim()) return grouped;
    const q = query.toLowerCase();
    const result: Record<WalkthroughGroup, WalkthroughEntry[]> = {
      "start-here": [],
      foundations: [],
      corpus: [],
      analysis: [],
      output: [],
    };
    for (const group of WALKTHROUGH_GROUP_ORDER) {
      result[group] = grouped[group].filter((w) => {
        return (
          w.content.label.toLowerCase().includes(q) ||
          (w.content.tagline?.toLowerCase().includes(q) ?? false) ||
          GROUP_LABELS[group].toLowerCase().includes(q)
        );
      });
    }
    return result;
  }, [grouped, query]);

  const hasAnyMatches = WALKTHROUGH_GROUP_ORDER.some((g) => filtered[g].length > 0);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-black/20 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className="fixed inset-y-0 right-0 z-50 flex w-[440px] max-w-full flex-col bg-guide-surface text-guide-text shadow-xl focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right"
          onEscapeKeyDown={(e) => {
            if (query) {
              e.preventDefault();
              setQuery("");
            }
          }}
        >
          {/* Header */}
          <div className="flex items-start justify-between border-b border-guide-border p-5">
            <div>
              <DialogPrimitive.Title className="text-base font-semibold text-guide-text">
                Guides
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="mt-0.5 text-xs text-guide-text-muted">
                Learn how PaperChase works
              </DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close asChild>
              <button aria-label="Close" className="text-guide-text-faint hover:text-guide-text-soft">
                <X className="size-5" />
              </button>
            </DialogPrimitive.Close>
          </div>

          {/* Replay quick start tour */}
          <div className="px-5 pt-3">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                useQuickStartStore.getState().replay();
              }}
              className="text-xs text-purple-600 hover:underline"
            >
              Replay quick start tour
            </button>
          </div>

          {/* Search */}
          <div className="px-5 pb-2 pt-4">
            <div className="flex items-center gap-2 rounded-md bg-guide-panel px-3 py-2">
              <Search className="size-3.5 text-guide-text-faint" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search guides..."
                aria-label="Search guides"
                autoFocus
                className="flex-1 bg-transparent text-xs outline-none"
              />
            </div>
          </div>

          {/* Entries */}
          <div className="flex-1 overflow-y-auto py-2">
            {!hasAnyMatches && (
              <div className="p-5 text-center text-xs text-guide-text-muted">
                No guides match "{query}".
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="ml-1 text-purple-600 hover:underline"
                >
                  Clear search
                </button>
              </div>
            )}
            {WALKTHROUGH_GROUP_ORDER.map((group) => {
              const items = filtered[group];
              if (items.length === 0) return null;
              return (
                <div key={group}>
                  <h3 className="px-5 pb-1 pt-4 text-[10px] font-bold uppercase tracking-wider text-guide-text-faint">
                    {GROUP_LABELS[group]}
                  </h3>
                  {items.map((w) => {
                    const isStartHere = group === "start-here";
                    const isSoon = w.content.status === "soon";
                    return (
                      <button
                        key={w.id}
                        type="button"
                        onClick={() => openWalkthrough(w.id)}
                        className={cn(
                          "flex w-full items-center justify-between px-5 py-2 text-left hover:bg-guide-surface-alt",
                          isStartHere &&
                            "mr-2 rounded-r-md border-l-[3px] border-purple-600 bg-gradient-to-r from-purple-100 to-guide-surface",
                        )}
                      >
                        <div>
                          <div
                            className={cn(
                              "text-xs",
                              isStartHere
                                ? "font-semibold text-guide-text"
                                : "font-medium text-guide-text-soft",
                            )}
                          >
                            {isStartHere && "✨ "}
                            {w.content.label}
                          </div>
                          <div className="mt-0.5 text-[10px] text-guide-text-muted">
                            {w.content.tagline}
                          </div>
                        </div>
                        {isSoon ? (
                          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold text-amber-700">
                            SOON
                          </span>
                        ) : (
                          <span className="rounded bg-guide-panel px-1.5 py-0.5 text-[10px] text-guide-text-muted">
                            {w.content.estMinutes} min
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* Footer: visibility toggle */}
          <GuideVisibilityFooter />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function readExpertFlag(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return JSON.parse(localStorage.getItem("paperchase-guide-user") ?? "{}").expert === true;
  } catch {
    return false;
  }
}

function GuideVisibilityFooter() {
  const [isHidden, setIsHidden] = useState<boolean>(() => readExpertFlag());
  const [suggestionsOff, setSuggestionsOff] = useState<boolean>(() => areSuggestionsDisabled());

  useEffect(() => {
    function handler() {
      setIsHidden(readExpertFlag());
    }
    window.addEventListener("guide:phase-change", handler);
    return () => window.removeEventListener("guide:phase-change", handler);
  }, []);

  useEffect(() => {
    function handler() {
      setSuggestionsOff(areSuggestionsDisabled());
    }
    window.addEventListener(SUGGESTIONS_PREF_EVENT, handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener(SUGGESTIONS_PREF_EVENT, handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  function toggleSuggestions(off: boolean) {
    setSuggestionsDisabled(off);
    setSuggestionsOff(off);
  }

  function setHidden(hidden: boolean) {
    const current = (() => {
      try {
        return JSON.parse(localStorage.getItem("paperchase-guide-user") ?? "{}");
      } catch {
        return {};
      }
    })();
    localStorage.setItem(
      "paperchase-guide-user",
      JSON.stringify({ ...current, expert: hidden }),
    );
    setIsHidden(hidden);
    window.dispatchEvent(new CustomEvent("guide:phase-change"));
  }

  function resetAllGuideState() {
    const keys = Object.keys(localStorage).filter(
      (k) =>
        k.startsWith("paperchase-guide-") ||
        k.startsWith("paperchase-quickstart-"),
    );
    for (const k of keys) localStorage.removeItem(k);
    // Also clear Guided Run state — sticky oneShot, per-concept dismiss, fired.
    for (const key of Object.keys(localStorage)) {
      if (
        key.startsWith("paperchase-guided-run-dismissed-") ||
        key.startsWith("paperchase-guided-run-oneshot-")
      ) {
        localStorage.removeItem(key);
      }
    }
    for (const key of Object.keys(sessionStorage)) {
      if (key.startsWith("paperchase-guided-run-fired-")) {
        sessionStorage.removeItem(key);
      }
    }
    window.location.reload();
  }

  // ---- Dev-only phase + cycle test controls --------------------------------
  // Eliminates DevTools-paste friction during smoke testing.

  function currentConceptId(): string | null {
    const m = window.location.pathname.match(/\/concepts\/([^/]+)/);
    return m?.[1] ?? null;
  }

  function setPhase(phase: "onboarding" | "graduated" | "expert") {
    // Carries _devForcedPhase so useGuideState's phase computation pins to
    // it AND skips its auto-graduation effect (which would otherwise
    // immediately flip a count-complete concept back to graduated).
    // For Onboarding, also zero-stub the server counts so the bar shows
    // the "Step 1 of 6: Seed" UX instead of "all complete".
    const next =
      phase === "onboarding"
        ? {
            _devForcedPhase: "onboarding" as const,
            _devForcedFreshCounts: true,
          }
        : phase === "graduated"
          ? { _devForcedPhase: "graduated" as const, graduated: true }
          : { _devForcedPhase: "expert" as const, graduated: true, expert: true };
    localStorage.setItem("paperchase-guide-user", JSON.stringify(next));

    // Wipe the per-concept just-unlocked queue. Without this, switching
    // between Force Onboarding (fresh counts) and Force Graduated (real
    // counts) makes useGuideState see every feature go locked→unlocked
    // in a single render, flooding the GuidedRunCard with unlock cards
    // and burying the graduation announcement.
    const cid = currentConceptId();
    if (cid) {
      localStorage.removeItem(`paperchase-guide-just-unlocked-${cid}`);
    }

    // Close drawer so JourneyBar morph + any one-shot card (graduation)
    // is visible.
    useGuidesStore.getState().setCatalogOpen(false);

    // No reload — useGuideState listens for guide:phase-change and re-reads
    // userStorage in-place. Lets onboarding→graduated transitions fire the
    // graduation announcement card without losing page state.
    window.dispatchEvent(new CustomEvent("guide:phase-change"));
    window.dispatchEvent(new CustomEvent("guide:guided-run-reset"));
  }

  function toggleCycleSignal(
    key: "hasRefinedConcept" | "hasRescoredAfterRefine" | "hasRunCoverageAudit" | "hasDrilledIntoCamp",
  ) {
    const cid = currentConceptId();
    if (!cid) return;
    const sigKey = `paperchase-guide-signals-${cid}`;
    let current: Record<string, unknown> = {};
    try {
      current = JSON.parse(localStorage.getItem(sigKey) ?? "{}");
    } catch {
      // ignore
    }
    current[key] = !current[key];
    localStorage.setItem(sigKey, JSON.stringify(current));
    // useGuideState re-reads signals on every render; dispatching the
    // phase-change event triggers a re-render so the arc transitions
    // visibly without a full reload.
    window.dispatchEvent(new CustomEvent("guide:phase-change"));
  }

  function clearOneShotFired() {
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith("paperchase-guided-run-oneshot-")) {
        localStorage.removeItem(key);
      }
    }
    for (const key of Object.keys(sessionStorage)) {
      if (key.startsWith("paperchase-guided-run-fired-")) {
        sessionStorage.removeItem(key);
      }
    }
    // No reload — dispatch the reset event so GuidedRunCard clears its
    // internal active state and re-scans triggers from a clean slate.
    window.dispatchEvent(new CustomEvent("guide:guided-run-reset"));
  }

  function resetCycleSignals() {
    const cid = currentConceptId();
    if (!cid) return;
    const sigKey = `paperchase-guide-signals-${cid}`;
    let current: Record<string, unknown> = {};
    try {
      current = JSON.parse(localStorage.getItem(sigKey) ?? "{}");
    } catch {
      // ignore
    }
    delete current.hasRefinedConcept;
    delete current.hasRescoredAfterRefine;
    delete current.hasRunCoverageAudit;
    delete current.hasDrilledIntoCamp;
    localStorage.setItem(sigKey, JSON.stringify(current));
    window.dispatchEvent(new CustomEvent("guide:phase-change"));
  }

  function simulateUnlock(featureId: string) {
    const cid = currentConceptId();
    if (!cid) return;
    // Seed the just-unlocked queue.
    localStorage.setItem(
      `paperchase-guide-just-unlocked-${cid}`,
      JSON.stringify([featureId]),
    );
    // Clear oneshot fired log so the synthetic unlock trigger can fire.
    localStorage.removeItem(`paperchase-guided-run-oneshot-${cid}`);
    localStorage.removeItem(`paperchase-guided-run-dismissed-${cid}`);
    sessionStorage.removeItem(`paperchase-guided-run-fired-${cid}`);
    // No reload — useGuideState re-reads justUnlocked on guide:phase-change.
    // The reset event tells GuidedRunCard to clear its active state so a new
    // card can fire even if one was already showing.
    window.dispatchEvent(new CustomEvent("guide:phase-change"));
    window.dispatchEvent(new CustomEvent("guide:guided-run-reset"));
    // Close the drawer so the GuidedRunCard (z-40, bottom-right) isn't
    // hidden behind the drawer (z-50).
    useGuidesStore.getState().setCatalogOpen(false);
  }

  return (
    <div className="border-t border-guide-border bg-guide-panel p-4 text-center text-[10px] text-guide-text-muted">
      <div className="mb-1 flex items-center justify-center gap-2">
        <span>Journey bar:</span>
        <div className="inline-flex overflow-hidden rounded border border-guide-border-strong">
          <button
            type="button"
            onClick={() => setHidden(false)}
            className={cn(
              "px-2 py-0.5 text-[10px]",
              !isHidden ? "bg-guide-text text-guide-surface" : "bg-guide-surface text-guide-text-muted",
            )}
          >
            Shown
          </button>
          <button
            type="button"
            onClick={() => setHidden(true)}
            className={cn(
              "px-2 py-0.5 text-[10px]",
              isHidden ? "bg-guide-text text-guide-surface" : "bg-guide-surface text-guide-text-muted",
            )}
          >
            Hidden
          </button>
        </div>
      </div>
      <div>Feature milestones teach specific tools inline on the journey bar.</div>

      <div className="mb-1 mt-3 flex items-center justify-center gap-2">
        <span>Workflow suggestions:</span>
        <div className="inline-flex overflow-hidden rounded border border-guide-border-strong">
          <button
            type="button"
            onClick={() => toggleSuggestions(false)}
            className={cn(
              "px-2 py-0.5 text-[10px]",
              !suggestionsOff ? "bg-guide-text text-guide-surface" : "bg-guide-surface text-guide-text-muted",
            )}
          >
            On
          </button>
          <button
            type="button"
            onClick={() => toggleSuggestions(true)}
            className={cn(
              "px-2 py-0.5 text-[10px]",
              suggestionsOff ? "bg-guide-text text-guide-surface" : "bg-guide-surface text-guide-text-muted",
            )}
          >
            Off
          </button>
        </div>
      </div>
      <div>Proactive nudges (e.g. “debate ready — audit coverage?”) shown bottom-right.</div>

      {import.meta.env.DEV && <DevPhaseControls
        currentConceptId={currentConceptId}
        setPhase={setPhase}
        toggleCycleSignal={toggleCycleSignal}
        clearOneShotFired={clearOneShotFired}
        resetCycleSignals={resetCycleSignals}
        simulateUnlock={simulateUnlock}
        resetAllGuideState={resetAllGuideState}
      />}
    </div>
  );
}

interface DevPhaseControlsProps {
  currentConceptId: () => string | null;
  setPhase: (p: "onboarding" | "graduated" | "expert") => void;
  toggleCycleSignal: (k: "hasRefinedConcept" | "hasRescoredAfterRefine" | "hasRunCoverageAudit" | "hasDrilledIntoCamp") => void;
  clearOneShotFired: () => void;
  resetCycleSignals: () => void;
  simulateUnlock: (featureId: string) => void;
  resetAllGuideState: () => void;
}

function DevPhaseControls({
  currentConceptId,
  setPhase,
  toggleCycleSignal,
  clearOneShotFired,
  resetCycleSignals,
  simulateUnlock,
  resetAllGuideState,
}: DevPhaseControlsProps) {
  const cid = currentConceptId();
  // Track signals in React state so the ✓ marks update reactively after the
  // toggle / reset buttons mutate localStorage. Without this, the buttons
  // mutate state but the visual ✓ flags lag until the next unrelated render.
  const readSignals = () => {
    if (!cid) return {};
    try {
      return JSON.parse(localStorage.getItem(`paperchase-guide-signals-${cid}`) ?? "{}");
    } catch {
      return {};
    }
  };
  const [signals, setSignals] = useState<Record<string, unknown>>(readSignals);
  useEffect(() => {
    function refresh() {
      setSignals(readSignals());
    }
    window.addEventListener("guide:phase-change", refresh);
    return () => window.removeEventListener("guide:phase-change", refresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cid]);
  const refineOn = !!signals.hasRefinedConcept && !!signals.hasRescoredAfterRefine;
  const auditOn = !!signals.hasRunCoverageAudit;
  const drillOn = !!signals.hasDrilledIntoCamp;

  return (
    <div className="mt-3 border-t border-guide-border-strong pt-3 text-left">
      <div className="mb-1.5 text-center text-[10px] font-semibold uppercase tracking-wider text-purple-700">
        [Dev] Phase & cycle controls
      </div>

      <div className="mb-2">
        <div className="mb-0.5 text-[10px] text-guide-text-muted">Force phase</div>
        <div className="inline-flex w-full overflow-hidden rounded border border-guide-border-strong">
          <button
            type="button"
            onClick={() => setPhase("onboarding")}
            className="flex-1 bg-guide-surface px-2 py-0.5 text-[10px] text-guide-text-soft hover:bg-guide-surface-alt"
          >
            Onboarding
          </button>
          <button
            type="button"
            onClick={() => setPhase("graduated")}
            className="flex-1 border-l border-guide-border-strong bg-guide-surface px-2 py-0.5 text-[10px] text-guide-text-soft hover:bg-guide-surface-alt"
          >
            Graduated
          </button>
          <button
            type="button"
            onClick={() => setPhase("expert")}
            className="flex-1 border-l border-guide-border-strong bg-guide-surface px-2 py-0.5 text-[10px] text-guide-text-soft hover:bg-guide-surface-alt"
          >
            Expert
          </button>
        </div>
      </div>

      <div className="mb-2">
        <div className="mb-0.5 text-[10px] text-guide-text-muted">
          Toggle cycle signals {cid ? "(per concept)" : "(no concept in URL — visit /concepts/<id>/expand)"}
        </div>
        <div className="grid grid-cols-3 gap-1">
          <button
            type="button"
            disabled={!cid}
            onClick={() => {
              toggleCycleSignal("hasRefinedConcept");
              toggleCycleSignal("hasRescoredAfterRefine");
            }}
            className={cn(
              "rounded border px-2 py-0.5 text-[10px]",
              refineOn ? "border-indigo-300 bg-indigo-100 text-indigo-900" : "border-guide-border-strong bg-guide-surface text-guide-text-soft hover:bg-guide-surface-alt",
              !cid && "opacity-40",
            )}
            title="Toggles both hasRefinedConcept and hasRescoredAfterRefine"
          >
            Refine {refineOn ? "✓" : ""}
          </button>
          <button
            type="button"
            disabled={!cid}
            onClick={() => toggleCycleSignal("hasRunCoverageAudit")}
            className={cn(
              "rounded border px-2 py-0.5 text-[10px]",
              auditOn ? "border-cyan-300 bg-cyan-100 text-cyan-900" : "border-guide-border-strong bg-guide-surface text-guide-text-soft hover:bg-guide-surface-alt",
              !cid && "opacity-40",
            )}
          >
            Audit {auditOn ? "✓" : ""}
          </button>
          <button
            type="button"
            disabled={!cid}
            onClick={() => toggleCycleSignal("hasDrilledIntoCamp")}
            className={cn(
              "rounded border px-2 py-0.5 text-[10px]",
              drillOn ? "border-violet-300 bg-violet-100 text-violet-900" : "border-guide-border-strong bg-guide-surface text-guide-text-soft hover:bg-guide-surface-alt",
              !cid && "opacity-40",
            )}
          >
            Drill {drillOn ? "✓" : ""}
          </button>
        </div>
        <button
          type="button"
          disabled={!cid}
          onClick={resetCycleSignals}
          className={cn(
            "mt-1 text-[10px] text-purple-600 hover:underline",
            !cid && "opacity-40",
          )}
          title="Clears all 4 cycle-enactment signals for the current concept (no reload)"
        >
          Reset cycle signals
        </button>
      </div>

      <div className="mb-2">
        <div className="mb-0.5 text-[10px] text-guide-text-muted">Simulate feature unlock</div>
        <div className="inline-flex w-full overflow-hidden rounded border border-guide-border-strong">
          <button
            type="button"
            disabled={!cid}
            onClick={() => simulateUnlock("field-debate")}
            className={cn(
              "flex-1 bg-guide-surface px-2 py-0.5 text-[10px] text-guide-text-soft hover:bg-guide-surface-alt",
              !cid && "opacity-40",
            )}
            title="Seeds just-unlocked queue + clears oneshot fired (no reload)"
          >
            Field Debate
          </button>
          <button
            type="button"
            disabled={!cid}
            onClick={() => simulateUnlock("coverage-audit")}
            className={cn(
              "flex-1 border-l border-guide-border-strong bg-guide-surface px-2 py-0.5 text-[10px] text-guide-text-soft hover:bg-guide-surface-alt",
              !cid && "opacity-40",
            )}
          >
            Coverage Audit
          </button>
          <button
            type="button"
            disabled={!cid}
            onClick={() => simulateUnlock("writing-workspace")}
            className={cn(
              "flex-1 border-l border-guide-border-strong bg-guide-surface px-2 py-0.5 text-[10px] text-guide-text-soft hover:bg-guide-surface-alt",
              !cid && "opacity-40",
            )}
          >
            Writing Workspace
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-1 text-center">
        <button
          type="button"
          onClick={clearOneShotFired}
          className="text-[10px] text-purple-600 hover:underline"
          title="Clears oneshot fired log so unlock + graduation cards re-fire"
        >
          Clear one-shot fired log
        </button>
        <button
          type="button"
          onClick={() => {
            // Drop ALL dev overrides (phase + fresh counts) but keep any
            // natural state (graduated / expert flags). Phase computation
            // falls back to count-driven logic.
            const current = (() => {
              try {
                return JSON.parse(localStorage.getItem("paperchase-guide-user") ?? "{}");
              } catch {
                return {};
              }
            })();
            const { _devForcedPhase, _devForcedFreshCounts, ...rest } = current;
            void _devForcedPhase;
            void _devForcedFreshCounts;
            localStorage.setItem("paperchase-guide-user", JSON.stringify(rest));
            window.dispatchEvent(new CustomEvent("guide:phase-change"));
          }}
          className="text-[10px] text-purple-600 hover:underline"
          title="Drops dev phase + fresh-counts overrides; phase reverts to count-driven natural behavior"
        >
          Clear forced phase (revert to natural)
        </button>
        <button
          type="button"
          onClick={resetAllGuideState}
          className="text-[10px] text-purple-600 hover:underline"
          title="Clears all paperchase-guide-*, paperchase-quickstart-*, paperchase-guided-run-* state, then reloads"
        >
          Reset all guide + tour state
        </button>
      </div>
    </div>
  );
}
